import * as Sentry from "@sentry/react";
import { orderBy, uniq } from "lodash";
import chunk from "lodash/chunk";

import { Accreditation } from "constants/draftingAttributes";
import { SaleType } from "constants/sale";
import { KEYWORD_CATEGORY_CRIPPLE } from "constants/saleLots";
import { SentrySeverityWarning } from "constants/sentry";

import { toTitleCase } from "lib";

import { sortPenByOrder } from "lib/auctionPens";
import { sortNumericalStrings } from "lib/compare";
import { isIOSApp } from "lib/devices";
import { getCombinedLotNumber } from "lib/saleLot";
import { isSentryActive } from "lib/sentry/config";
import { formatDate, formatTime, placardDateFormat } from "lib/timeFormats";
import toast from "lib/toast";

import logo from "img/AgriNous-logo-base64";

import { isLpaAccredited } from "./properties";

export const BookingCardVersions = {
  PreSale: "pre-sale",
  PostSale: "post-sale",
  Stud: "stud",
  SignOff: "sign-off",
  SignOffCompact: "sign-off-compact",
  SaleyardCode: "sy-code",
};

export const breakword = (name, maxLength = 24) => {
  if (name.length > maxLength) {
    return `${name.substring(0, maxLength)}...`;
  } else {
    return name;
  }
};

export function calculateEstimateOfHowManyLines(str, charsPerLine = 10) {
  const words = str.split(" ");
  let lines = 1;
  let lineChars = 0;
  for (const word of words) {
    if (word.length > charsPerLine) {
      lines++;
      lineChars = word.length - charsPerLine;
    } else if (lineChars + word.length > charsPerLine) {
      lines++;
      lineChars = word.length;
    } else {
      lineChars += word.length;
    }
  }
  return lines;
}

/**
 * Get auction pen quantities in format for pdfMake
 * @param {object} auctionPen
 * @return {object}
 */
export const getQuantityDetails = auctionPen => {
  const stack = [
    {
      text: `${auctionPen.sale_lots.reduce(
        (sum, lot) => sum + lot.quantity,
        0,
      )}`,
      style: "mainText",
      alignment: "right",
    },
  ];
  if (auctionPen.sale_lots[0].overflowQuantity) {
    stack.push({
      text: `(${auctionPen.sale_lots[0].overflowQuantity})`,
      style: "smallText",
      alignment: "right",
    });
  }
  return { stack };
};

// In order to keep the auction pen data and the following blank
// row (which is used for note-taking) together, these are rendered
// as a nested table, taking advantage of the outer table's
// dontBreakRows property.
/**
 * Get pen details in format for pdfMake
 * @param {object} auctionPen
 * @return {object}
 */
export const getBookingPenDetails = (
  auctionPen,
  showLotNumber = true,
  landscape,
  index,
  includeRound = true,
  includeFullSizePenRange = false,
) => {
  const stack = [];
  const penRound = auctionPen?.sale_lots[0]?.sale_round?.name;

  const overFlowPens = uniq(
    auctionPen?.sale_lots.map(lot => lot.overflowPen).filter(Boolean),
  );
  if (auctionPen.start_pen) {
    const showFullPenRange = includeFullSizePenRange && auctionPen.end_pen;
    const penText = `${auctionPen.start_pen}${
      showFullPenRange ? `-${auctionPen.end_pen}` : ""
    }`;
    stack.push({
      text: penText,
      style: "mainText",
    });
  }

  if (!includeFullSizePenRange && auctionPen.end_pen) {
    stack.push({
      text: `${auctionPen.end_pen}`,
      style: "smallText",
    });
  }
  if (overFlowPens.length > 0) {
    stack.push({
      text: `O/F to: \n ${overFlowPens.join(", ")}`,
      style: "smallText",
    });
  }
  if (showLotNumber && !landscape) {
    auctionPen.sale_lots.forEach(lot => {
      stack.push({
        text: `Lot#${getCombinedLotNumber(lot)}`,
        style: "smallText",
      });
    });
  }

  if (includeRound && penRound) {
    stack.push({
      text: penRound,
      style: "smallText",
    });
  }

  if (index > 0) {
    stack.push({
      text: `continued`,
      style: "smallText",
    });
  }

  return { stack };
};

export const getClearingSaleLotBookingDetails = saleLot => {
  const stack = [];

  const penText = saleLot.lotNumberCombined;
  stack.push({
    text: penText,
    style: "mainText",
  });

  return { stack };
};

export const dashBetweenLots = {
  hLineStyle: (i, node) => {
    if (i === 0 || i === node.table.body.length) {
      return null;
    }
    return { dash: { length: 5, space: 2 } };
  },
  hLineWidth: (i, node) => {
    if (i === 0 || i === node.table.body.length) {
      return 0;
    }
    return 1;
  },
  vLineWidth: () => 0,
};

const getSaleLotDetails = (
  lot,
  options = { includeWeight: true, includeTagAndName: false },
) => {
  const details = ["breed", "sex", "age"].reduce((arr, attr) => {
    lot[attr].name && arr.push(lot[attr].name);
    return arr;
  }, []);

  details.push(
    orderBy(lot.marks, "order")
      .map(mark => mark.location)
      .join(", "),
  );

  if (options.includeWeight) {
    details.push(
      lot.averageWeightFormatted ? `${lot.averageWeightFormatted}kg` : null,
    );
  }
  if (options.includeTagAndName) {
    details.push(
      lot.draftingAttributes ? lot.draftingAttributes.animalName : null,
    );
    details.push(
      lot.draftingAttributes ? lot.draftingAttributes.tagNumber : null,
    );
  }

  return details.filter(Boolean).join(" - ");
};

/**
 * Get vendor details in format for pdfMake
 * @return {object}
 * @param lot
 * @param options
 */
export const getVendorDetails = (
  lot,
  options = {
    includeWeight: true,
    includeShortCodeSaleyard: false,
    includeVendorNumber: false,
    extraSpacing: false,
    showVendorInfo: true,
    includeTagAndName: false,
    includeEuStatus: false,
    lineX1: 0,
    lineX2: 0,
  },
  landscape = false,
  cattlePortrait = false,
  isClearingSale = false,
) => {
  const firstSpace = options.extraSpacing && lot.quantity >= 2;
  const secondSpace =
    options.extraSpacing && !isClearingSale && lot.quantity >= 3;
  const thirdSpace =
    options.extraSpacing && !isClearingSale && lot.quantity > 3;
  const x1 = options.lineX1 || 0;
  let x2 = options.lineX2;
  if (!x2) {
    if (landscape) {
      x2 = 740;
    } else if (cattlePortrait) {
      x2 = 500;
    } else {
      x2 = 395;
    }
  }
  const wordLength = landscape ? 20 : 13;
  const vendorText = `${
    options.extraSpacing
      ? breakword(lot.vendor.name, wordLength)
      : lot.vendor.name
  }${
    options.includeShortCodeSaleyard && lot.vendor.shortCodeSaleyard
      ? ` - ${lot.vendor.shortCodeSaleyard}`
      : ""
  }${
    options.includeVendorNumber && lot.consignment.vendorNumber
      ? `: ${lot.livestock_agency_code}${lot.consignment.vendorNumber}`
      : ""
  }${options.includeEuStatus && lot.euStatus ? " **EU**" : ""}`;

  return [
    {
      text: vendorText,
      style:
        calculateEstimateOfHowManyLines(vendorText) < 3
          ? "mainText"
          : "middleText",
    },
    options.showVendorInfo && {
      text: [
        { text: lot.quantity, bold: true },
        {
          text: ` ${
            getSaleLotDetails(lot, {
              includeWeight: options.includeWeight,
              includeTagAndName: options.includeTagAndName,
            }) || "-"
          }`,
          bold: true,
        },
      ],
      style: "smallText",
    },
    options.extraSpacing &&
      [
        {
          text: " ",
          style: "smallText",
        },
        firstSpace &&
          [
            {
              canvas: [
                {
                  type: "line",
                  x1,
                  y1: 0,
                  x2,
                  y2: 0,
                  dash: { length: 5, space: 2 },
                },
              ],
            },
            {
              text: " ",
              style: "middleText",
            },
            {
              text: " ",
              style: "middleText",
            },
          ].filter(Boolean),
        secondSpace &&
          [
            {
              canvas: [
                {
                  type: "line",
                  x1,
                  y1: 0,
                  x2,
                  y2: 0,
                  dash: { length: 5, space: 2 },
                },
              ],
            },
            {
              text: " ",
              style: "middleText",
            },
            {
              text: " ",
              style: "middleText",
            },
          ].filter(Boolean),
        thirdSpace && [
          {
            canvas: [
              {
                type: "line",
                x1,
                y1: 0,
                x2,
                y2: 0,
                dash: { length: 5, space: 2 },
              },
            ],
          },
          {
            text: " ",
            style: "middleText",
          },
          {
            text: " ",
            style: "smallSpacedText",
          },
        ],
      ].filter(Boolean),
  ].filter(Boolean);
};

export const getPriceDetails = lot => ({
  text: lot.price > 0 ? lot.price : " ",
  style: "middleText",
});

export const getBuyerDetails = (lot, extraSpacing, buyerNameMaxLength = 20) => [
  {
    text: extraSpacing
      ? breakword(lot.buyer_name || " ", buyerNameMaxLength)
      : lot.buyer_name || " ",
    style:
      calculateEstimateOfHowManyLines(lot.buyer_name) < 2
        ? "middleText"
        : "smallTextBold",
  },
  {
    text: lot.buyer_way ? lot.buyer_way.name : " ",
    style:
      calculateEstimateOfHowManyLines(lot.buyer_name) < 2
        ? "middleText"
        : "smallTextBold",
  },
];

export function getDescriptionText(saleLot, showTagAndNameInDescriptionFirst) {
  const draftingAttributes = saleLot.draftingAttributes || {};
  return [
    ...(showTagAndNameInDescriptionFirst
      ? [
          draftingAttributes.tagNumber
            ? `Tag ${draftingAttributes.tagNumber}`
            : null,
          draftingAttributes.animalName || null,
        ]
      : []),
    saleLot.breed_name,
    saleLot.sex_name,
    saleLot.age_group_name,
    ...(showTagAndNameInDescriptionFirst
      ? []
      : [
          draftingAttributes.animalName || "",
          draftingAttributes.tagNumber || "",
        ]),
  ]
    .filter(Boolean)
    .join(" - ");
}

const baseNestedColumnWidths = [180, 60, 120];
const baseNestedLandscapeColumnWidths = [180, 150, 100, 120];
const signOffReportNestedLandscapeColumnsWidths = [30, 25, 180, 150, 60, 120];
const clearingSaleBookingCardNestedColumnsWidths = [25, 180, 150, 60, 230];

export const makeAuctionPenRow = (
  auctionPen,
  saleLots,
  index,
  showComment,
  blankRowHeight,
  extraSpacing,
  columnWidths,
  landscape,
  includeTagAndName = false,
  isSignOffReport,
  showVendorInfo,
  showTagAndNameInDescriptionFirst,
  showSaleyardCode = false,
) => {
  const rows = [];
  let nestedWidths = baseNestedColumnWidths;
  if (landscape) {
    nestedWidths = baseNestedLandscapeColumnWidths;
  }
  if (isSignOffReport) {
    nestedWidths = signOffReportNestedLandscapeColumnsWidths;
  }
  rows.push([
    getBookingPenDetails(
      auctionPen,
      false,
      landscape,
      index,
      false,
      isSignOffReport,
    ),
    ...(!isSignOffReport ? [getQuantityDetails(auctionPen)] : []),
    {
      layout: dashBetweenLots,
      table: {
        widths: nestedWidths,
        body: saleLots.map(sl => {
          return [
            ...(isSignOffReport ? [sl.quantity] : []),
            ...(isSignOffReport
              ? [
                  isLpaAccredited(sl.consignment.vendor_property.lpaStatus)
                    ? "Y"
                    : "N",
                ]
              : []),
            getVendorDetails(
              sl,
              {
                extraSpacing,
                showVendorInfo,
                includeTagAndName,
                includeShortCodeSaleyard: showSaleyardCode,
              },
              landscape,
            ),
            ...(landscape
              ? [getDescriptionText(sl, showTagAndNameInDescriptionFirst)]
              : []),
            getPriceDetails(sl),
            getBuyerDetails(sl, extraSpacing),
          ];
        }) || [" ", " ", " ", " ", "", " "],
      },
    },
    "",
    "",
    ...(landscape ? [""] : []),
    ...(isSignOffReport ? ["", ""] : []),
  ]);

  if (showComment) {
    rows.push([{ text: "", colSpan: columnWidths.length }]);
  }

  return {
    border: [false, false, false, false],
    marginTop: -6,
    table: {
      headerRows: 0,
      widths: columnWidths,
      heights(row) {
        return row === 0 ? "auto" : blankRowHeight;
      },
      body: rows,
    },
  };
};

export const makeClearingSaleLotRow = (
  saleLot,
  showComment,
  blankRowHeight,
  extraSpacing,
  columnWidths,
) => {
  const rows = [];
  const nestedWidths = clearingSaleBookingCardNestedColumnsWidths;

  rows.push([
    saleLot.lotNumberCombined,
    {
      layout: dashBetweenLots,
      table: {
        widths: nestedWidths,
        body: [
          [
            saleLot.quantity,
            getVendorDetails(
              saleLot,
              {
                extraSpacing,
                showVendorInfo: false,
                lineX1: -95,
                lineX2: 700,
              },
              true,
              false,
              true,
            ),
            saleLot.clearingSaleAttributes.title,
            getPriceDetails(saleLot),
            getBuyerDetails(saleLot, extraSpacing, 65),
          ],
        ],
      },
    },
    "",
    "",
    "",
    "",
  ]);

  if (showComment) {
    rows.push([{ text: "", colSpan: columnWidths.length }]);
  }

  return {
    border: [false, false, false, false],
    marginTop: -6,
    table: {
      headerRows: 0,
      widths: columnWidths,
      heights(row) {
        return row === 0 ? "auto" : blankRowHeight;
      },
      body: rows,
    },
  };
};

/**
 * Get pen details in format for pdfMake
 * @param {object} auctionPen
 * @return {object}
 */
export const getPenDetails = auctionPen => {
  const stack = [];
  if (auctionPen.start_pen) {
    let penNumber = `${auctionPen.start_pen}`;
    if (auctionPen.end_pen) {
      penNumber += ` - ${auctionPen.end_pen}`;
    }
    stack.push({
      text: penNumber,
      style: "mainText",
    });
  }
  if (auctionPen.sale_lots[0].overflowPen) {
    stack.push({
      text: `O/F to ${auctionPen.sale_lots[0].overflowPen}`,
      style: "smallText",
    });
  }
  return { stack };
};

export const getCattleTableData = (
  auctionPens,
  blankRowHeight,
  extraSpacing,
  makeCattleAuctionPenRow,
  getSaleLotsPerPageCount,
  vertical = false,
  isPostSaleReport,
  isSignOffReport,
  showSaleyardCode = false,
) =>
  auctionPens.reduce((arr, auctionPen) => {
    // WHen there are more salelots than would fit on a full page, we need to
    // split them over multiple rows (visually) because of a bug in pdfmake
    // where when dontBreakRows is set to true it will just hide the row
    // if its impossible to satisfy that condition.
    const saleLotChunks = chunk(
      auctionPen.sale_lots,
      getSaleLotsPerPageCount(extraSpacing),
    );
    saleLotChunks.forEach((saleLots, index) => {
      const showComment = vertical
        ? saleLots.length === 1 || index === saleLotChunks.length - 1
        : false;
      arr.push([
        makeCattleAuctionPenRow(
          auctionPen,
          saleLots,
          showComment,
          blankRowHeight,
          extraSpacing,
          isPostSaleReport,
          isSignOffReport,
          showSaleyardCode,
        ),
      ]);
    });

    return arr;
  }, []);

// It would have been easier to insert a page break when hitting the
// is lane_start, however page break in nested tables doesn't work wth
// PDFmake.  So we create a new content block for each lane.
export const getLanes = auctionPens => {
  let lane = 0;
  const lanes = [[]];
  sortPenByOrder(auctionPens).forEach((auctionPen, i) => {
    if (i > 0 && auctionPen.isLaneStart) {
      lane += 1;
      lanes.push([]);
    }
    lanes[lane].push(auctionPen);
  });
  return lanes;
};

export const getTableData = (
  auctionPens,
  blankRowHeight,
  extraSpacing,
  columnWidths,
  getSaleLotsPerPageCount,
  landscape = false,
  includeTagAndName = false,
  isSignOffReport,
  showVendorInfo,
  showTagAndNameInDescriptionFirst,
  showSaleyardCode = false,
) =>
  auctionPens.reduce((arr, auctionPen) => {
    // When there are more salelots than would fit on a full page, we need to
    // split them over multiple rows (visually) because of a bug in pdfmake
    // where when dontBreakRows is set to true it will just hide the row
    // if its impossible to satisfy that condition.
    const saleLotChunks = chunk(
      auctionPen.sale_lots,
      getSaleLotsPerPageCount(extraSpacing),
    );
    saleLotChunks.forEach((saleLots, index) => {
      const showComment =
        (saleLots.length === 1 || index === saleLotChunks.length - 1) &&
        !showTagAndNameInDescriptionFirst;

      // For sign off reports we want to exclude lots with the cripple category (animal health) selected.
      let filteredLots = saleLots;
      let shouldIncludePen = true;
      if (isSignOffReport) {
        filteredLots = saleLots.filter(
          lot => lot.category_name !== KEYWORD_CATEGORY_CRIPPLE.toUpperCase(),
        );
        shouldIncludePen = filteredLots.length > 0;
      }
      if (shouldIncludePen) {
        arr.push([
          makeAuctionPenRow(
            auctionPen,
            filteredLots,
            index,
            showComment,
            blankRowHeight,
            extraSpacing,
            columnWidths,
            landscape,
            includeTagAndName,
            isSignOffReport,
            showVendorInfo,
            showTagAndNameInDescriptionFirst,
            showSaleyardCode,
          ),
        ]);
      }
    });

    return arr;
  }, []);

export const getClearingSaleTableData = (
  saleLots,
  blankRowHeight,
  extraSpacing,
  columnWidths,
) => {
  return sortNumericalStrings(
    Object.values(saleLots),
    saleLot => saleLot.lotNumberCombined,
  ).reduce((arr, saleLot) => {
    arr.push([
      makeClearingSaleLotRow(
        saleLot,
        false,
        blankRowHeight,
        extraSpacing,
        columnWidths,
      ),
    ]);

    return arr;
  }, []);
};

export const getWeightDetails = (lot, includeTotalWeight = false) =>
  [
    {
      text: lot.averageWeightFormatted ? `${lot.averageWeightFormatted}` : "-",
      style: "middleText",
    },
    includeTotalWeight && {
      text: lot.total_weight ? `${lot.total_weight}` : "-",
      style: "middleText",
    },
  ].filter(Boolean);

const getSaleInformation = (auctionPens, saleLots, sale, agencyName, round) => {
  const isClearingSale = sale.sale_type === SaleType.CLEARING;

  const lots = isClearingSale
    ? saleLots
    : auctionPens.flatMap(pen => pen.sale_lots);

  const totalQuantity = lots.reduce((total, lot) => total + lot.quantity, 0);
  // Clearing sale saleyard name is: 'Clearing Sale'
  // Appending 'Sale' to this would be redundant
  const appendSaleText = isClearingSale ? "" : " Sale";
  const countLabel = isClearingSale ? "QTY" : "HC";

  return `${agencyName.toUpperCase()} - ${sale.saleyard_name}${
    round ? ` ${toTitleCase(round.name)}` : ""
  }${appendSaleText} ${formatDate(
    sale.date,
  )} - ${countLabel} ${totalQuantity} - Printed: ${formatTime(Date.now())}`;
};

/**
 * Returns a header/footer function formatted for pdfMake
 * @param {array} auctionPens
 * @param {object} sale
 * @param {string} agencyName
 * @param {object} round
 * @param {number} pageMargin
 * @param {boolean} includeLogo
 * @param {string} logoName
 * @return {function}
 */
export const getSaleAndDocumentInformation =
  (
    auctionPens,
    sale,
    agencyName,
    round,
    pageMargin,
    includeLogo,
    logoName,
    saleLots = [],
  ) =>
  (page, pages) => ({
    columns: [
      {
        image: `data:image/jpeg;base64,${logo}`,
        width: 100,
      },
      {
        width: "auto",
        alignment: "left",
        text: getSaleInformation(
          auctionPens,
          saleLots,
          sale,
          agencyName,
          round,
        ),
        marginTop: 4,
        marginLeft: 10,
      },
      ...(includeLogo && logoName
        ? [
            {
              image: logoName,
              width: 130,
              absolutePosition: { x: 600, y: 10 },
            },
          ]
        : []),
      {
        width: "*",
        alignment: "right",
        text: [{ text: `${page}` }, " of ", { text: `${pages}` }],
        marginTop: 4,
      },
    ],
    margin: [pageMargin + 3, 0],
  });

export const tick = (name, x, y, sizeX, sizeY) => {
  if (name) {
    return [
      {
        image:
          "data:image/png;base64,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",
        absolutePosition: { x, y },
        height: sizeX ? sizeY : 80,
        width: sizeY || 70,
      },
    ];
  }
};

export const blackBox = (
  name,
  y1,
  y2,
  deployment,
  x1,
  x2,
  x3,
  noTick,
  width = 210,
  height = 100,
) => {
  return [
    {
      canvas: [
        {
          type: "rect",
          x: 1,
          y: 1,
          w: noTick ? 285 : width,
          h: height,
          fillOpacity: 1,
          color: deployment.corporateColor
            ? deployment.corporateColor
            : "#000000",
        },
      ],
      absolutePosition: { x: x1, y: y1 },
    },
    {
      canvas: [
        {
          type: "rect",
          x: 1,
          y: 1,
          w: noTick ? 0 : 75,
          h: noTick ? 0 : height - 1,
        },
      ],
      absolutePosition: { x: x3, y: y1 },
    },
    {
      text: name,
      fontSize: 40,
      width: 30,
      color: "white",
      absolutePosition: { x: x2, y: y2 },
    },
  ];
};

export const addSpaces = numberOfSpaces =>
  [...Array(numberOfSpaces)].map(() => [
    {
      text: " ",
    },
  ]);

const textBox = (idx, x1, y1, deployment, noTick, isA4) => {
  let xPosition = x1;
  const xIncrement = isA4 ? 180 : 250;
  if (idx === 1) {
    xPosition = x1 + xIncrement;
  } else if (idx === 2) {
    xPosition = x1 + xIncrement * 2;
  } else {
    xPosition = x1;
  }
  const position = isA4
    ? {
        x: xPosition,
        y: y1,
      }
    : {
        x: xPosition,
        y: y1,
      };

  const width = noTick ? 285 : isA4 ? 100 : 170;
  const height = isA4 ? 80 : 100;
  return {
    canvas: [
      {
        type: "rect",
        x: 1,
        y: 1,
        w: width,
        h: height,
        lineColor: "black",
        fillOpacity: 1,
        color: deployment.corporateColor
          ? deployment.corporateColor
          : "#000000",
      },
    ],
    absolutePosition: position,
  };
};

const tickBox = (idx, x3, y1, noTick, isA4) => {
  let xPosition = x3;
  const xIncrement = isA4 ? 180 : 250;
  if (idx === 1) {
    xPosition = x3 + xIncrement;
  } else if (idx === 2) {
    xPosition = x3 + xIncrement * 2;
  } else {
    xPosition = x3;
  }
  const position = isA4
    ? {
        x: xPosition,
        y: y1,
      }
    : {
        x: xPosition,
        y: y1,
      };

  const width = isA4 ? 65 : 75;
  const height = isA4 ? 79 : 99;
  return {
    canvas: [
      {
        type: "rect",
        x: 1,
        y: 1,
        w: noTick ? 0 : width,
        h: noTick ? 0 : height,
      },
    ],
    absolutePosition: position,
  };
};

const boxText = (idx, x2, y1, name, isA4) => {
  let xPosition = x2;
  const xIncrement = isA4 ? 180 : 250;
  if (idx === 1) {
    xPosition = x2 + xIncrement;
  } else if (idx === 2) {
    xPosition = x2 + xIncrement * 2;
  } else {
    xPosition = x2;
  }

  let yPosition = y1;
  if (idx === 1) {
    yPosition = y1 + 22;
  } else if (idx === 2) {
    yPosition = isA4 ? y1 + 12 : y1 + 5;
  } else {
    yPosition = isA4 ? y1 + 12 : y1 + 5;
  }

  const position = {
    x: xPosition,
    y: yPosition,
  };
  const width = isA4 ? 10 : 30;
  return {
    text: name.toUpperCase(),
    fontSize: isA4 ? 22 : 40,
    width,
    color: "white",
    bold: true,
    absolutePosition: position,
    pageBreak: idx === 2 ? "after" : "none",
  };
};

const groupedTicks = (isVendorBred, isWeaned, isGrassFed, isA4) => {
  const x = isA4 ? 135 : 235;
  const y = isA4 ? 710 : 1010;

  const xIncrement = isA4 ? 180 : 250;

  const sizeX = isA4 ? 60 : null;
  const sizeY = isA4 ? 60 : null;

  return [
    tick(isVendorBred, x, y, sizeX, sizeY),
    tick(isWeaned, x + xIncrement, y, sizeX, sizeY),
    tick(isGrassFed, x + xIncrement * 2, y, sizeX, sizeY),
  ];
};

export const groupedBoxes = (
  names,
  y1,
  deployment,
  x1,
  x2,
  x3,
  noTick,
  isVendorBred,
  isWeaned,
  isGrassFed,
  isA4,
) => {
  const content = [];

  names.forEach((name, idx) => {
    content.push(
      textBox(idx, x1, y1, deployment, noTick, isA4),
      tickBox(idx, x3, y1, noTick, isA4),
      boxText(idx, x2, y1, name, isA4),
      idx === 1 ? groupedTicks(isVendorBred, isWeaned, isGrassFed, isA4) : [],
    );
  });

  return content;
};

export const fontSize = (word, isA4, customMap = false) => {
  // Depending on the amount of char in the word it will get the font size
  // The maps determine the size (5:120  = If 5 chars, font size is 120)
  const a3Map = {
    5: 120,
    10: 110,
    15: 80,
    20: 70,
    25: 60,
    35: 50,
  };
  const a4Map = {
    5: 100,
    10: 90,
    15: 60,
    20: 45,
    25: 40,
    35: 35,
  };

  let map = {};
  if (isA4) {
    map = a4Map;
  } else if (customMap) {
    map = customMap;
  } else {
    map = a3Map;
  }

  // Find the biggest font size that is ok.
  const max = Object.entries(map).map(([num, fontSize]) => {
    if (word.length < num) {
      return fontSize;
    } else {
      return 0;
    }
  });
  if (Math.max(...max) === 0) {
    return 60;
  } else {
    return Math.max(...max);
  }
};

const getDropText = saleLot => {
  const { dropStart, dropEnd } = saleLot.draftingAttributes;
  if (dropStart) {
    const dropRange = [
      // Show year on drop start if there is no drop end.
      placardDateFormat(dropStart, !dropEnd),
      placardDateFormat(dropEnd, true),
    ]
      .filter(Boolean)
      .join(" / ");

    return `${dropRange} DROP`;
  } else {
    return "";
  }
};

export const getSheepInformation = saleLot => {
  if (!saleLot.draftingAttributes) {
    return "";
  }
  return `${getDropText(saleLot)}${
    saleLot.draftingAttributes.shearing
      ? ` ${placardDateFormat(saleLot.draftingAttributes.shearing)} SHORN\n`
      : ""
  }${
    saleLot.draftingAttributes.xBloodline
      ? ` B/line: ${saleLot.draftingAttributes.xBloodline}\n`
      : ""
  }${
    saleLot.draftingAttributes.bySire
      ? `Sire: ${saleLot.draftingAttributes.bySire}`
      : ""
  }${
    saleLot.draftingAttributes.joinedEnd
      ? `\n ${placardDateFormat(
          saleLot.draftingAttributes.joinedStart,
        )} / ${placardDateFormat(
          saleLot.draftingAttributes.joinedEnd,
          true,
        )} JOINED`
      : ""
  }${
    saleLot.draftingAttributes.sil
      ? ` ${parseInt(saleLot.draftingAttributes.sil, 10).toFixed(0)}% SIL`
      : ""
  }`.toUpperCase();
};

export const getSecondarySheepInformation = saleLot => {
  if (!saleLot.draftingAttributes) {
    return "";
  }
  return `${
    saleLot.draftingAttributes.minMassGrams
      ? `${
          saleLot.draftingAttributes.maxMassGrams
            ? `${
                Math.round(saleLot.draftingAttributes.minMassGrams) / 1000
              }kg to`
            : `${Math.round(saleLot.draftingAttributes.minMassGrams) / 1000}kg`
        }`
      : ""
  } ${
    saleLot.draftingAttributes.maxMassGrams
      ? `${Math.round(saleLot.draftingAttributes.maxMassGrams) / 1000}kg`
      : ""
  }  ${
    saleLot.averageWeightFormattedRounded
      ? `@ ${saleLot.averageWeightFormattedRounded}kg Ave`
      : ""
  }`.toUpperCase();
};

let pdfInitializationFailures = 0;
export function createAndOpenPdf(
  pdfDefinition,
  routerHistory,
  suppressBack = false,
) {
  const openedInNewWindow = window.history.length === 1;

  const { pdfMake } = window;
  if (!pdfMake || !pdfMake.createPdf) {
    pdfInitializationFailures += 1;
    if (pdfInitializationFailures < 3) {
      toast.warning(
        "Generating of PDF is taking longer than expected. Try again shortly.",
      );
    } else {
      // 2 failures and we're probably not going to get there; alert the user it's all fubar and probably not going to work, and reset the counter
      toast.error(
        "Something went wrong generating your PDF.  Please refresh and try again.",
      );
      pdfInitializationFailures = 0;
    }

    // if this report has been opened in a new window, close it after the pdf is printed as it is shown in another window
    if (openedInNewWindow) {
      window.close();
      // otherwise send them back.
    } else if (!suppressBack) {
      if (routerHistory) {
        routerHistory.goBack();
      } else {
        window.history.back();
      }
    }

    if (isSentryActive) {
      Sentry.captureMessage(
        `PDFMake not initialized. pdfMake ${
          pdfMake ? "On Window" : "Not Found"
        }.  createPDF ${
          pdfMake.createPdf ? "On window.pdfMake" : "Not Found"
        }.  ${pdfInitializationFailures} failed attempts.`,
        SentrySeverityWarning,
      );
    }
    return;
  }
  const pdf = pdfMake.createPdf(pdfDefinition);
  let printWindow = null;
  if (window.Cypress) {
    pdf.download();
  } else {
    printWindow = window.open("", "_blank");
    try {
      if (isIOSApp()) {
        pdf.download();
      } else {
        pdf.print({}, printWindow);
      }
    } catch (e) {
      pdf.open({}, printWindow);
    }
  }

  // if this report has been opened in a new window, close it after the pdf is printed as it is shown in another window
  if (openedInNewWindow) {
    window.close();
  } else if (!suppressBack) {
    if (routerHistory) {
      routerHistory.goBack();
    } else {
      window.history.back();
    }
  }
}

export function getFirstTime(lot, scans) {
  if (lot.timeWeighed) {
    return ["Weighed: ", formatTime(lot.timeWeighed)];
  } else if (scans.length > 0) {
    return ["First Wt:", formatTime(scans[0])];
  }
  return ["", ""];
}

export function getLastTime(lot, scans) {
  if (scans.length > 1) {
    return ["Last Wt:", formatTime(scans[scans.length - 1])];
  }
  return ["", ""];
}

export function getPensByAgentId(auctionPens, agentId) {
  return auctionPens.filter(pen =>
    pen.sale_lots.some(saleLot => `${saleLot.livestockAgency?.id}` === agentId),
  );
}

export function getBreedAgeSexText(sl) {
  // If the breed, age and sex name is long and needs to wrap,
  // we want to keep the age and sex together and wrap them down a line

  const defaultText = [sl?.breed_name, sl?.age_group_name, sl?.sex_name]
    .filter(Boolean)
    .join(" ");

  if (defaultText.length > 20) {
    // If the text is longer we don't need to filter and join like above
    return `${sl?.breed_name}\n ${sl.age_group_name} ${sl.sex_name}`;
  }

  return defaultText;
}

export function getVendorName(saleLot, business) {
  const displayName = business.publicDisplayName || business.name;
  return saleLot.vendor.isPublished || business.isPublished
    ? displayName || saleLot.vendor_name
    : "Withheld";
}

export const getHgpDetails = sl => {
  const declaration = sl?.consignment?.declaration;
  const isTreated = sl?.consignment?.declaration?.treated_with_hgp;
  const isEU = sl?.draftingAttributes?.accreditationEU === Accreditation.ALL;
  let hgp = "";

  if (declaration) {
    if (isTreated) {
      hgp = "Y";
    } else if (isEU || !isTreated) {
      hgp = "N";
    } else {
      hgp = "?";
    }
  }
  return {
    text: hgp,
    bold: true,
  };
};

export const preparePlacardLogos = (sale, deploymentPlacardLogos) => {
  return sale.deployment_sales.reduce((acc, deploymentSale) => {
    acc[`logo_${deploymentSale.deployment_id}`] =
      deploymentPlacardLogos[deploymentSale.deployment_id];
    return acc;
  }, {});
};
